import React from 'react';
import dompurify from 'dompurify';

// Hooks
import { useBaProfileDrawer } from 'Shared/utils/hooks/use-ba-profile-drawer';

// Types
import { User } from 'Shared/constants/types';
import {
  BAFactorShapeType,
  BehavioralPatternGraphType,
} from 'Shared/components/BehavioralPatternGraph/FactorShape.service';
import { InspireReport } from 'Shared/types/InspireReport.types';

// Constants
import ExternalLinksConstants from 'Shared/constants/ExternalLinks.constants';
import ColorConstants from 'Wpds/colors/ColorConstants';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useBaProfile } from 'Shared/utils/hooks/use-ba-profile';
import { useBaTracking } from 'Shared/tracking/use-ba-tracking';
import { useInspireReports } from 'Shared/utils/hooks/use-inspire-reports';
import { useSmac } from 'Shared/utils/hooks/use-smac';

// Components
import { WpIcon } from 'Wpds/components/icon';
import WpDivider from 'Wpds/components/divider/WpDivider';
import { WpButton } from 'Wpds/components/button';
import { WpLink } from 'Wpds/components/link';
import { WpCollapsible } from 'Wpds/components/collapsible';
import { WpText } from 'Wpds/components/text';
import BaDetailsListWrapper from '../_partials/BaDetailsListWrapper';
import BaProfileDisplayHeader from './BaProfileDisplayHeader';
import BehavioralPatternGraph from 'Shared/components/BehavioralPatternGraph/BehavioralPatternGraph';

// Styles
import './BaProfileDisplay.scss';

export type Props = {
  user: User;
  showStrengthsAndCaution?: boolean;
  /**
   * The shape type of the graph. If not provided, it will default to a blue
   * circle.
   */
  shapeType?: BAFactorShapeType;
  /**
   * Determines if a divider should be shown at the bottom of the component.
   * Disabling this can be helpful to reduce the amount of space the component
   * takes up.
   *
   * Defaults to true.
   */
  useDivider?: boolean;
  /**
   * Displays BA content
   *
   * Defaults to true.
   */
  showBaDetails?: boolean;
  /**
   * Displays conversation tips in place of the BA content
   *
   * Defaults to false.
   */
  showConversationTips?: boolean;
  /**
   * show button allowing BA to be opened in drawer
   *
   * Defaults to false.
   */
  showViewInDrawerButton?: boolean;
  showDownloadReportButton?: boolean;
  showLearnMoreLink?: boolean;
};

const BaProfileDisplay = ({
  user,
  showStrengthsAndCaution = true,
  shapeType = BAFactorShapeType.Circle,
  useDivider = true,
  showBaDetails = true,
  showConversationTips = false,
  showViewInDrawerButton = false,
  showDownloadReportButton = true,
  showLearnMoreLink = true,
}: Props): JSX.Element => {
  const { handleOpenBaDrawer } = useBaProfileDrawer({ user });

  const [isStrengthsAndCautionsOpen, toggleIsStrengthsAndCautionsOpen] = useToggle(false);
  const { baProfile, howToInteractWith, strengths, cautionAreas, preferredWorkStylesSummary, conversationTips } =
    useBaProfile({
      user,
      withFetch: false,
    });
  const {
    trackStrengthsAndCautionsClick,
    trackLearnMorePIBasicsClick,
    trackViewBehavioralReportButtonClick: trackViewAssessmentReportButtonClick,
  } = useBaTracking();

  const handleStrengthsAndCautionsClick = () => {
    toggleIsStrengthsAndCautionsOpen();
    trackStrengthsAndCautionsClick({ isStrengthsAndCautionsOpen });
  };

  const { getInspireReport } = useInspireReports({ surveyUserGuid: baProfile.assessmentUserId });
  const { hasInspireAccess } = useSmac();

  const isConversationTipsVisible = showConversationTips && hasInspireAccess;
  const showReportLink = hasInspireAccess && showDownloadReportButton;
  const showDocumentationMoreLink = showLearnMoreLink || !hasInspireAccess;

  const handleViewBaReportClick = async () => {
    trackLearnMorePIBasicsClick();
    trackViewAssessmentReportButtonClick();
    await getInspireReport(InspireReport.Behavioral);
  };

  const baseClassName = 'BaProfileDisplay';

  return (
    <div className={baseClassName}>
      <BaProfileDisplayHeader user={user} />
      <div className={`${baseClassName}__description`}>
        <p dangerouslySetInnerHTML={{ __html: dompurify.sanitize(baProfile.referencePattern.descriptionBolded) }} />
      </div>
      {isConversationTipsVisible && (
        <div
          className={`${baseClassName}__conversation-tips ${
            showBaDetails ? `${baseClassName}__conversation-tips--padding-bottom` : ''
          }`}
        >
          <div className={`${baseClassName}__conversation-tips__title`}>
            <div className={`${baseClassName}__conversation-tips__title__icon`}>
              <WpIcon name="lightbulb-on" size="lg" color={ColorConstants.blue[500]} />
            </div>
            <WpText bold noMargin>
              Conversation tips
            </WpText>
          </div>
          <WpText color="gray-600">When working with {user.firstName}, you might want to:</WpText>
          <div className={`${baseClassName}__conversation-tips__tips`}>
            {conversationTips.map(tip => (
              <div className={`${baseClassName}__conversation-tips__tips__tip`} key={tip.replaceAll(' ', '')}>
                <WpText noMargin color="gray-600" variant="bodySm">
                  {tip}
                </WpText>
              </div>
            ))}
          </div>
        </div>
      )}
      {(showBaDetails || (showConversationTips && !hasInspireAccess)) && (
        <>
          <div className={`${baseClassName}__mini-graph`}>
            <BehavioralPatternGraph
              baPersonInfo={baProfile}
              graphType={BehavioralPatternGraphType.Mini}
              shapeType={shapeType}
              useConnectingLine={true}
            />
          </div>
          <div>
            <BaDetailsListWrapper title="How to interact with me:" list={howToInteractWith} />
          </div>
          {showStrengthsAndCaution && (
            <>
              <div className={`${baseClassName}__collapsible`} role="button" onClick={handleStrengthsAndCautionsClick}>
                <p className={`${baseClassName}__collapsible__title`}>Strengths & Cautions</p>
                <WpIcon name={isStrengthsAndCautionsOpen ? 'chevron-up' : 'chevron-down'} />
              </div>

              <WpCollapsible open={isStrengthsAndCautionsOpen}>
                <WpDivider padding={8} />
                <BaDetailsListWrapper title="Preferred work style" list={preferredWorkStylesSummary} />
                <BaDetailsListWrapper title="Strengths" list={strengths} />
                <BaDetailsListWrapper title="Potential Caution Areas" list={cautionAreas} />
              </WpCollapsible>
            </>
          )}

          {(showReportLink || showDocumentationMoreLink) && (
            <>
              {useDivider && <WpDivider padding={16} />}
              <div className={`${baseClassName}__footer`}>
                {showReportLink && (
                  <WpButton onClick={handleViewBaReportClick} variant="link">
                    <WpIcon name="external-link" />
                    View Behavioral Report
                  </WpButton>
                )}
                {showDocumentationMoreLink && (
                  <WpLink href={ExternalLinksConstants.learn_more_about_ba}>
                    <WpIcon name="external-link" />
                    Learn how to interpret results
                  </WpLink>
                )}
              </div>
            </>
          )}
        </>
      )}
      {showViewInDrawerButton && hasInspireAccess && (
        <div className={`${baseClassName}__view-full-details-link`}>
          <WpButton variant="link" onClick={handleOpenBaDrawer}>
            View full details
          </WpButton>
        </div>
      )}
    </div>
  );
};

export default BaProfileDisplay;
